import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link, Navigate } from "react-router-dom";
import WorldMapHero from "@/components/WorldMapHero";
import { GoogleSignInButton, OrDivider } from "@/components/GoogleSignInButton";

export default function Login() {
  const { user, login, error, setError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    await login(email, password);
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <WorldMapHero />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-[#f7f5f0]">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="Pins" className="w-9 h-9 bg-[#f7f5f0]" />
            <span className="font-serif text-2xl">Pins</span>
          </div>
          <div>
            <p className="overline text-[#f7f5f0]/80">Chapter I</p>
            <h1 className="font-serif text-5xl lg:text-6xl leading-none mt-3 max-w-md">
              Every pin is a memory worth returning to.
            </h1>
            <p className="mt-6 font-sans text-[#f7f5f0]/85 max-w-md leading-relaxed">
              Log the coordinates of your favourite places, pin them to a warm-toned world map, and keep the notes and photographs that made them yours.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16 bg-[#f7f5f0]">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <p className="overline">Sign in</p>
          <h2 className="font-serif text-4xl lg:text-5xl leading-none mt-3">Return to your atlas.</h2>
          <p className="mt-4 text-[#5c5c5c]">Welcome back, traveller. Continue plotting your world.</p>

          <div className="mt-10 space-y-6">
            <GoogleSignInButton />
            <OrDivider />
            <div>
              <label className="field-label" htmlFor="email">Email</label>
              <input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                className="field-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="password">Password</label>
              <input
                id="password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                className="field-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <div className="mt-2 text-right">
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#5c5c5c] underline underline-offset-4 hover:text-[#2c4a3e]"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="border border-[#b85c47] px-3 py-2 text-sm font-mono text-[#963f2b]">
                {error}
              </div>
            )}

            <button data-testid="login-submit-button" className="btn-primary w-full" disabled={submitting}>
              {submitting ? "Signing in…" : "Enter Journal"}
            </button>
          </div>

          <div className="mt-8 flex items-center justify-between font-mono text-xs uppercase tracking-[0.2em] text-[#5c5c5c]">
            <span>New here?</span>
            <Link to="/register" data-testid="register-link" className="underline underline-offset-4 text-[#2c4a3e]">
              Create account →
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
